<template>
  <div class="load-orders-wrapper">
    <header class="page-header fade-in">
      <div>
        <h1>Ordres de chargement</h1>
        <p>
          Gérez chaque ordre de chargement, suivez les statuts et reprenez une
          préparation active à tout moment.
        </p>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline" @click="refresh" :disabled="loading">
          <i class="bi bi-arrow-clockwise"></i>
          <span>Rafraîchir</span>
        </button>
        <button class="btn btn-primary" @click="create">
          <i class="bi bi-plus-circle"></i>
          <span>Nouvel ordre</span>
        </button>
      </div>
    </header>

    <section class="page-body fade-in">
      <div class="filters">
        <div class="search">
          <i class="bi bi-search"></i>
          <input
            v-model.trim="searchTerm"
            type="search"
            placeholder="Rechercher par référence, client, statut..."
          />
        </div>
        <div class="stats" v-if="filteredLoadOrders.length">
          <span>{{ filteredLoadOrders.length }} enregistrement(s)</span>
        </div>
      </div>

      <div v-if="loading" class="state">
        <div class="loader"></div>
        <p>Chargement des ordres de chargement…</p>
      </div>

      <div v-else-if="!filteredLoadOrders.length" class="state">
        <i class="bi bi-journal-x"></i>
        <h3>Aucun ordre de chargement</h3>
        <p>
          Créez votre premier ordre pour commencer à suivre les expéditions.
        </p>
        <button class="btn btn-primary" @click="create">
          <i class="bi bi-plus-circle"></i>
          <span>Créer un ordre</span>
        </button>
      </div>

      <div v-else class="records-grid">
        <article
          v-for="entry in filteredLoadOrders"
          :key="entry.id"
          class="record-card fade-in"
        >
          <div class="card-header">
            <div class="identity">
              <strong class="customer-label">
                {{ entry.customerName || "Client inconnu" }}
              </strong>
              <small v-if="entry.reference">Ref. {{ entry.reference }}</small>
            </div>
            <span class="badge" :class="`badge--${getValidationStatus(entry)}`">
              {{ getValidationLabel(entry) }}
            </span>
          </div>

          <ul class="card-info">
            <li>
              <i class="bi bi-calendar2-event"></i>
              <span>
                {{ formatDate(entry.scheduledDate || entry.createdAt) }}
              </span>
            </li>
            <li>
              <i class="bi bi-currency-dollar"></i>
              <span>{{ formatCurrency(entry.totalAmount) }}</span>
            </li>
            <li>
              <i class="bi bi-box-seam"></i>
              <span
                >{{
                  entry.totalItems || entry.items?.length || 0
                }}
                article(s)</span
              >
            </li>
          </ul>

          <div v-if="entry.items && entry.items.length > 0" class="card-items">
            <div class="items-header">
              <strong>Articles :</strong>
            </div>
            <ul class="items-list">
              <li
                v-for="(item, index) in entry.items.slice(0, 3)"
                :key="item.id || item.reference || index"
                class="item-entry"
              >
                <span class="item-name">
                  {{
                    item.itemFullName ||
                    item.designation ||
                    item.item?.fullName ||
                    item.item?.name ||
                    item.reference ||
                    "Article"
                  }}
                </span>
                <span class="item-quantity">
                  {{ formatQuantity(item.quantity || item.qty || 0) }}
                  <small
                    class="item-price"
                    v-if="item.totalPriceTTC || item.total_price_ttc"
                  >
                    ({{
                      formatCurrency(
                        item.totalPriceTTC || item.total_price_ttc
                      )
                    }})
                  </small>
                </span>
              </li>
              <li v-if="entry.items.length > 3" class="item-entry more-items">
                <span
                  >+{{ entry.items.length - 3 }} article(s)
                  supplémentaires</span
                >
              </li>
            </ul>
          </div>

          <div class="card-footer">
            <div class="meta">
              <span v-if="entry.createdBy">
                <i class="bi bi-person-circle"></i>
                {{ entry.createdBy }}
              </span>
              <span v-if="entry.updatedAt">
                <i class="bi bi-clock-history"></i>
                {{ formatDate(entry.updatedAt) }}
              </span>
            </div>
            <div class="actions">
              <button class="btn btn-outline" @click="view(entry.reference)">
                <i class="bi bi-eye"></i>
                <span>Voir</span>
              </button>
              <button class="btn btn-outline" @click="edit(entry.reference)">
                <i class="bi bi-pencil"></i>
                <span>Modifier</span>
              </button>
              <button class="btn btn-danger" @click="remove(entry.reference)">
                <i class="bi bi-trash"></i>
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "LoadOrderList",
  data() {
    return {
      loading: false,
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("loadOrder", {
      loadOrders: "getAll",
    }),
    normalizedLoadOrders() {
      const source = Array.isArray(this.loadOrders?.data)
        ? this.loadOrders.data
        : Array.isArray(this.loadOrders)
        ? this.loadOrders
        : [];

      return source.map((raw) => {
        const reference =
          raw.reference || raw.code || raw.uuid || raw.id || "—";
        const items = raw.orderItems || raw.items || [];
        return {
          id: raw.id || raw.uuid || reference,
          reference,
          status: raw.status || raw.state || "draft",
          validated: raw.validated,
          customerName:
            raw.customerFullName ||
            raw.customerName ||
            raw.customer_name ||
            raw.customer?.fullName ||
            raw.customer?.name ||
            raw.customer_reference ||
            raw.customerReference ||
            "",
          totalAmount:
            Number(
              raw.totalPriceTTC != null
                ? raw.totalPriceTTC
                : raw.total_amount != null
                ? raw.total_amount
                : raw.totalAmount != null
                ? raw.totalAmount
                : raw.total != null
                ? raw.total
                : raw.amount != null
                ? raw.amount
                : 0
            ) || 0,
          scheduledDate:
            raw.date ||
            raw.scheduled_date ||
            raw.scheduledDate ||
            raw.due_date ||
            "",
          createdAt: raw.created_at || raw.createdAt || "",
          updatedAt: raw.updated_at || raw.updatedAt || "",
          totalItems:
            raw.totalQuantity != null
              ? raw.totalQuantity
              : raw.totalItems != null
              ? raw.totalItems
              : raw.items_count != null
              ? raw.items_count
              : items.length || null,
          items: items,
          createdBy: raw.userFullName || raw.userName || raw.created_by || "",
          raw,
        };
      });
    },
    filteredLoadOrders() {
      if (!this.searchTerm) return this.normalizedLoadOrders;
      const term = this.searchTerm.toLowerCase();
      return this.normalizedLoadOrders.filter((entry) => {
        const values = [
          entry.reference,
          entry.customerName,
          entry.status,
          entry.totalAmount,
          entry.scheduledDate,
          entry.createdAt,
        ]
          .filter((value) => value !== null && value !== undefined)
          .map((value) => value.toString().toLowerCase());
        return values.some((value) => value.includes(term));
      });
    },
  },
  methods: {
    async fetchLoadOrders() {
      this.loading = true;
      try {
        await this.$store.dispatch("loadOrder/getAll");
      } catch (error) {
        console.error("Impossible de charger les ordres", error);
        this.$toast?.error?.("Échec du chargement des ordres de chargement.");
      } finally {
        this.loading = false;
      }
    },
    refresh() {
      this.fetchLoadOrders();
    },
    create() {
      this.$router.push({ name: "loadorders-create" });
    },
    view(reference) {
      this.$router.push({
        name: "loadorders-show",
        params: { reference },
      });
    },
    edit(reference) {
      this.$router.push({
        name: "loadorders-edit",
        params: { reference },
      });
    },
    async remove(reference) {
      if (!reference) return;
      const confirmed = await this.$confirm?.({
        title: "Supprimer l'ordre",
        message:
          "Cet ordre de chargement sera supprimé définitivement. Continuer ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (!confirmed) return;
      try {
        await this.$store.dispatch("loadOrder/destroy", reference);
        this.$toast?.success?.("Ordre de chargement supprimé.");
      } catch (error) {
        console.error("Erreur lors de la suppression de l'ordre", error);
        this.$toast?.error?.("Impossible de supprimer l'ordre de chargement.");
      }
    },
    formatCurrency(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return value;
      }
    },
    statusClass(status = "") {
      return status.toLowerCase().replace(/\s+/g, "-") || "draft";
    },
    getValidationStatus(entry) {
      const validated = entry.validated;
      if (validated === true || validated === 1) {
        return "validated";
      } else if (validated === false || validated === 0) {
        return "cancelled";
      } else if (validated === null || validated === undefined) {
        return "pending";
      } else {
        return "pending";
      }
    },
    getValidationLabel(entry) {
      const validated = entry.validated;
      if (validated === true || validated === 1) {
        return "Validé";
      } else if (validated === false || validated === 0) {
        return "Annulé";
      } else if (validated === null || validated === undefined) {
        return "En attente";
      } else {
        return "En attente";
      }
    },
    formatQuantity(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      const hasDecimals = number % 1 !== 0;
      return hasDecimals ? number.toFixed(2) : number.toString();
    },
  },
  mounted() {
    if (!this.normalizedLoadOrders.length) {
      this.fetchLoadOrders();
    }
  },
};
</script>

<style scoped>
.load-orders-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fbff 0%, #eef2ff 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.4rem 0;
  font-size: 2.25rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #475569;
  max-width: 520px;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.2rem;
  border-radius: 16px;
  background: #f3f4f8;
  border: 1px solid #e4e7f1;
  min-width: 280px;
  flex: 1;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem;
  width: 100%;
}

.stats {
  color: #6b7280;
  font-weight: 600;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #6b7280;
  text-align: center;
}

.state i {
  font-size: 3rem;
  color: #2563eb;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.records-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.record-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), #ffffff 100%);
  border-radius: 20px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  max-height: 420px;
  overflow: hidden;
}

.record-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 46px rgba(15, 23, 42, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.customer-label {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
}

.card-info {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.4rem;
}

.card-info li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
  font-size: 0.9rem;
}

.card-info i {
  color: #2563eb;
}

.badge {
  padding: 0.3rem 0.9rem;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(148, 163, 184, 0.2);
  color: #475569;
}

.badge--draft {
  background: rgba(148, 163, 184, 0.25);
  color: #334155;
}

.badge--in-progress {
  background: rgba(59, 130, 246, 0.2);
  color: #1d4ed8;
}

.badge--completed {
  background: rgba(34, 197, 94, 0.2);
  color: #0f766e;
}

.badge--cancelled {
  background: rgba(239, 68, 68, 0.2);
  color: #b91c1c;
}

.badge--validated {
  background: rgba(34, 197, 94, 0.2);
  color: #0f766e;
}

.badge--pending {
  background: rgba(251, 191, 36, 0.2);
  color: #b45309;
}

.card-items {
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.15);
}

.items-header {
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.items-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 120px;
  overflow-y: auto;
}

.item-entry {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.5rem;
  background: white;
  border-radius: 8px;
  font-size: 0.85rem;
  border: 1px solid rgba(148, 163, 184, 0.1);
}

.item-name {
  flex: 1;
  color: #1e293b;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0.5rem;
}

.item-quantity {
  color: #2563eb;
  font-weight: 600;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.15rem;
}

.item-price {
  color: #64748b;
  font-weight: 400;
  font-size: 0.75rem;
}

.item-entry.more-items {
  justify-content: center;
  color: #64748b;
  font-style: italic;
  background: transparent;
  border: none;
  padding: 0.25rem;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-end;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
}

.meta {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: #64748b;
  font-size: 0.85rem;
}

.meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.7rem 1.05rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn i {
  font-size: 1.1rem;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

@media (max-width: 1024px) {
  .load-orders-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }

  .page-header {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .load-orders-wrapper {
    padding: 1.25rem 1rem 4rem;
  }

  .page-body {
    padding: 1.5rem;
  }

  .search {
    min-width: unset;
  }

  .records-grid {
    grid-template-columns: 1fr;
  }

  .card-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }

  .page-body {
    padding: 1.25rem;
  }

  .record-card {
    padding: 1.25rem;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .actions {
    flex-direction: column;
  }
}
</style>
